import React, { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';
import { initializeHLS, destroyHLS, getApiBaseUrl } from '../utils/hlsPlayer';
import './ChannelsPage.css';

const ChannelsPage = () => {
  const [channelsData, setChannelsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [playerModal, setPlayerModal] = useState({ show: false, channel: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const channelsPerPage = 40; // 4 channels per row, max 10 rows
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    fetchChannelsData();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLanguage, selectedProvider, searchTerm]);

  const fetchChannelsData = async () => {
    try {
      setLoading(true);
      
      // Fetch today's match channels from the working backend
      const data = await apiService.getChannelsPageData();
      
      if (data.success && data.channels) {
        console.log("Today's channels fetched successfully:", data);
        
        // Transform simple channels array to nested structure that template expects
        const transformedData = transformChannelsToNestedStructure(data.channels);
        setChannelsData(transformedData);
      } else {
        console.error('Failed to fetch channels:', data.error);
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform simple channels array to nested structure
  const transformChannelsToNestedStructure = (channelsArray) => {
    // Group channels by provider
    const providerGroups = {};
    
    channelsArray.forEach(channel => {
      // Use a default provider if not specified
      const providerName = channel.provider || 'Today\'s Channels';
      
      if (!providerGroups[providerName]) {
        providerGroups[providerName] = [];
      }
      
      providerGroups[providerName].push({
        id: channel.name,
        name: channel.name,
        url: channel.streamUrl || `${getApiBaseUrl()}/api/channel/${encodeURIComponent(channel.name)}`,
        category: 'Football',
        provider: providerName
      });
    });

    // Create the nested structure that the template expects
    return {
      success: true,
      languages: ['unified'],
      channels: {
        unified: {
          providers: providerGroups
        }
      }
    };
  };

  const getLanguageDisplayName = (lang) => {
    const names = {
      english: "English",
      french: "French",
      spanish: "Spanish", 
      arabic: "Arabic",
      unified: "All Providers",
      german: "German",
      italian: "Italian",
      portuguese: "Portuguese",
      dutch: "Dutch",
      polish: "Polish",
      turkish: "Turkish"
    };
    return names[lang] || lang.charAt(0).toUpperCase() + lang.slice(1);
  };

  const playChannel = (channel) => {
    // Check if it's a Legacy4K HLS stream
    const isLegacy4K = channel.url && channel.url.includes('legacy4k.tv');
    
    // For Legacy4K, use HLS proxy instead of restream
    let streamUrl;
    if (isLegacy4K) {
      streamUrl = `${getApiBaseUrl()}/api/hls-proxy?url=${encodeURIComponent(channel.url)}`;
    } else if (channel.url && channel.url.startsWith('http://')) {
      streamUrl = `${getApiBaseUrl()}/api/restream?url=${encodeURIComponent(channel.url)}`;
    } else {
      streamUrl = channel.url || `${getApiBaseUrl()}/api/channel/${encodeURIComponent(channel.name)}`;
    }
    
    setPlayerModal({ show: true, channel: { ...channel, proxyUrl: streamUrl, isHLS: isLegacy4K } });
    
    // Initialize player after modal opens
    setTimeout(() => {
      if (videoRef.current && streamUrl) {
        loadStream(streamUrl, isLegacy4K);
      }
    }, 100);
  };

  const loadStream = (streamUrl, isHLS = false) => {
    // Cleanup previous HLS instance
    destroyHLS(hlsRef.current);
    hlsRef.current = null;

    if (!videoRef.current) {
      console.error('Video element not available');
      return;
    }

    const backendUrl = streamUrl.startsWith('http') ? streamUrl : `${getApiBaseUrl()}${streamUrl}`;
    console.log('Loading stream:', backendUrl, 'isHLS:', isHLS);
    
    // For HLS proxy content or M3U8 streams, use shared HLS initialization
    if (streamUrl.includes('/api/hls-proxy') || isHLS) {
      console.log('Using shared HLS.js for stream');
      hlsRef.current = initializeHLS(videoRef.current, backendUrl, {
        maxRetries: 2,
        autoPlay: true
      });
      return;
    }
    
    // For restreamed content, use direct video element
    if (streamUrl.includes('/api/restream')) {
      console.log('Using direct playback for restreamed content');
      videoRef.current.src = streamUrl;
      videoRef.current.type = 'video/mp4';
      
      videoRef.current.onloadedmetadata = () => {
        console.log('Video metadata loaded');
        videoRef.current.play().catch(e => console.log('Autoplay blocked:', e));
      };
      
      videoRef.current.onerror = (e) => {
        console.log('Video error, trying HEVC fallback:', e);
        // Try HEVC transcoding fallback
        const hevcUrl = streamUrl.replace('/api/restream?', '/api/restream/hevc?');
        console.log('Falling back to HEVC transcoding:', hevcUrl);
        videoRef.current.src = hevcUrl;
        videoRef.current.load();
      };
      
      videoRef.current.load();
      return;
    }

    // For all other streams (IPTV TS, direct M3U8, etc.), use shared HLS initialization
    console.log('Loading IPTV stream with shared HLS');
    hlsRef.current = initializeHLS(videoRef.current, backendUrl, {
      maxRetries: 2,
      autoPlay: true,
      onError: (data) => {
        // On fatal error, fallback to direct stream
        console.log('HLS failed, trying direct stream fallback');
        playDirectStream(streamUrl);
      }
    });
  };

  const playDirectStream = (streamUrl) => {
    console.log('Using direct video element for stream');
    const backendUrl = streamUrl.startsWith('http') ? streamUrl : `${getApiBaseUrl()}${streamUrl}`;
    console.log('Using backend URL for direct stream:', backendUrl);
    videoRef.current.src = backendUrl;
    videoRef.current.type = 'video/mp2t';
    
    let retryCount = 0;
    const maxRetries = 3;
    
    videoRef.current.onerror = () => {
      retryCount++;
      if (retryCount <= maxRetries) {
        console.log(`Retry attempt ${retryCount}/${maxRetries}`);
        setTimeout(() => {
          videoRef.current.load();
        }, 1000 * retryCount);
      } else {
        console.log('Max retries reached, stream unavailable');
      }
    };
    
    videoRef.current.onloadeddata = () => {
      console.log('Stream data loaded');
      videoRef.current.play().catch(e => console.log('Autoplay blocked'));
    };
    
    videoRef.current.load();
  };

  const closePlayer = () => {
    destroyHLS(hlsRef.current);
    hlsRef.current = null;
    setPlayerModal({ show: false, channel: null });
  };

  const getPaginatedChannels = (channels) => {
    const startIndex = (currentPage - 1) * channelsPerPage;
    const endIndex = startIndex + channelsPerPage;
    return channels.slice(startIndex, endIndex);
  };

  const getTotalPages = (channels) => {
    return Math.ceil(channels.length / channelsPerPage);
  };

  const filteredChannels = () => {
    if (!channelsData) {
      console.log('No channelsData available');
      return {};
    }
    
    if (!channelsData.channels) {
      console.log('No channels in channelsData:', channelsData);
      return {};
    }
    
    let filtered = { ...channelsData.channels };
    
    // Filter by language
    if (selectedLanguage !== 'all') {
      filtered = { [selectedLanguage]: filtered[selectedLanguage] };
    }
    
    // Filter by provider and search term
    Object.keys(filtered).forEach(lang => {
      const langData = { ...filtered[lang] };
      const providers = { ...langData.providers };
      
      Object.keys(providers).forEach(providerName => {
        let channels = [...providers[providerName]];
        
        // Filter by provider
        if (selectedProvider !== 'all' && !providerName.toLowerCase().includes(selectedProvider.toLowerCase())) {
          delete providers[providerName];
          return;
        }
        
        // Filter by search term
        if (searchTerm) {
          channels = channels.filter(channel => 
            channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            channel.category?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        
        if (channels.length > 0) {
          providers[providerName] = channels;
        } else {
          delete providers[providerName];
        }
      });
      
      if (Object.keys(providers).length > 0) {
        filtered[lang] = { ...langData, providers };
      } else {
        delete filtered[lang];
      }
    });
    
    return filtered;
  };

  const getUniqueProviders = () => {
    if (!channelsData) return [];
    
    const providers = new Set();
    Object.values(channelsData.channels).forEach(langData => {
      Object.keys(langData.providers).forEach(provider => {
        providers.add(provider);
      });
    });
    
    return Array.from(providers).sort();
  };

  if (loading) {
    return (
      <div className="channels-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading channels...</p>
        </div>
      </div>
    );
  }

  if (!channelsData) {
    return (
      <div className="channels-page">
        <div className="error">
          <h2>Failed to load channels</h2>
          <button onClick={fetchChannelsData}>Try Again</button>
        </div>
      </div>
    );
  }

  const filtered = filteredChannels();

  return (
    <div className="channels-page">
      <div className="channels-header">
        <h1>VFootball Channels</h1>
      </div>

      <div className="channels-filters">
        <div className="filter-group">
          <label>Language:</label>
          <select 
            value={selectedLanguage} 
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            <option value="all">All Languages</option>
            {channelsData?.languages?.map(language => (
              <option key={language} value={language}>
                {getLanguageDisplayName(language)}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Provider:</label>
          <select 
            value={selectedProvider} 
            onChange={(e) => setSelectedProvider(e.target.value)}
          >
            <option value="all">All Providers</option>
            {getUniqueProviders().map(provider => (
              <option key={provider} value={provider}>
                {provider}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search channels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="channels-content">
        {Object.keys(filtered).length === 0 ? (
          <div className="no-results">
            <h3>No channels found</h3>
            <p>Try adjusting your filters or search term</p>
          </div>
        ) : (
          Object.entries(filtered).map(([language, langData]) => (
            <div key={language} className="language-section">
              <h2 className="language-title">
                {getLanguageDisplayName(language)}
                <span className="channel-count">
                  ({langData.providers ? Object.values(langData.providers).flat().length : 0} channels)
                </span>
              </h2>

              {langData.providers && Object.entries(langData.providers).map(([providerName, channels]) => (
                <div key={providerName} className="provider-section">
                  <h3 className="provider-title">
                    {providerName}
                    <span className="provider-count">({channels ? channels.length : 0} channels)</span>
                  </h3>

                  <div className="channels-grid">
                    {channels && getPaginatedChannels(channels).map((channel, index) => (
                      <div key={`${channel?.id || index}-${index}`} className="channel-card">
                        <div className="channel-info">
                          <h4 className="channel-name" title={channel?.name || 'Unknown'}>
                            {channel?.name || 'Unknown Channel'}
                          </h4>
                          {channel?.category && (
                            <p className="channel-category">{String(channel.category)}</p>
                          )}
                        </div>
                        
                        <div className="channel-actions">
                          <button 
                            className="play-btn"
                            onClick={() => playChannel(channel)}
                            title="Play in our player"
                          >
                            Play
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {channels && getTotalPages(channels) > 1 && (
                    <div className="pagination">
                      <button 
                        className="pagination-btn"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        ← Previous
                      </button>
                      
                      <span className="pagination-info">
                        Page {currentPage} of {getTotalPages(channels)}
                      </span>
                      
                      <button 
                        className="pagination-btn"
                        onClick={() => setCurrentPage(Math.min(getTotalPages(channels), currentPage + 1))}
                        disabled={currentPage === getTotalPages(channels)}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {playerModal.show && (
        <div className="player-modal" onClick={closePlayer}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{playerModal.channel?.name || 'Unknown Channel'}</h3>
              <button className="close-btn" onClick={closePlayer}>×</button>
            </div>
            <div className="player-container">
              <video 
                ref={videoRef}
                width="100%" 
                height="400" 
                controls 
                playsInline
                muted={false}
                onError={(e) => console.log('Video error:', e)}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="channel-details">
              <p><strong>Category:</strong> {playerModal.channel?.category || 'N/A'}</p>
              <p><strong>Provider:</strong> {playerModal.channel?.provider || 'Unknown'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelsPage;